import React, { useEffect, useRef } from 'react';
import type { Transaction } from '../types';
import TransactionItem from './TransactionItem';

interface TransactionListProps {
  transactions: Transaction[];
  totalCount?: number;
  loadedCount?: number;
  title?: string;
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

export default function TransactionList({
  transactions,
  totalCount = 0,
  loadedCount = 0,
  title = '成交记录',
  isLoading = false,
  onLoadMore,
  hasMore = false,
}: TransactionListProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasMore || isLoading || !onLoadMore) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, onLoadMore]);

  if (!isLoading && transactions.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-secondary">暂无成交记录</p>
      </div>
    );
  }

  return (
    <div>
      {title && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-medium text-primary">{title}</h3>
          {totalCount > 0 && (
            <span className="text-xs text-secondary">
              已加载 {loadedCount} / {totalCount} 条
            </span>
          )}
        </div>
      )}

      <div className="space-y-3">
        {transactions.map((transaction) => (
          <TransactionItem key={transaction.soldId} transaction={transaction} />
        ))}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center gap-2 py-6">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
          <span className="text-sm text-secondary">加载中...</span>
        </div>
      )}

      {hasMore && !isLoading && <div ref={sentinelRef} className="h-1" />}

      {!hasMore && !isLoading && transactions.length > 0 && (
        <div className="text-center py-4">
          <span className="text-xs text-secondary-light">— 已加载全部 —</span>
        </div>
      )}
    </div>
  );
}
